/**
 * System Status — the roll-up diagram topology (Flow L Frame 1; ADR-035 / S6).
 *
 * Pure, React-free. The fixed schematic — node coordinates (viewBox
 * ``0 0 1070 562``), the nine data-flow edges, and the **composed** interface
 * stacks — transcribed from the storyboard mock
 * (`docs/engineering/detail/storyboards/flows/L-system-status/index.html`),
 * which is the **coordinate source of record**: change that file first, then
 * transcribe this one from its header. Bespoke-SVG topology (storyboard OQ-2
 * lean: small fixed graph, air-gap-clean, no graph library).
 *
 * # The v2 layout (architect reference, approved 2026-08-01)
 *
 * Four rows, each on one centre line (y 56 / 190 / 356 / 508)::
 *
 *     row 1                    User Interface Manager
 *     row 2   Tasking Manager · Time Series Manager · Processed Data Store
 *     row 3   Ingress · Message Bus · Egress                ← the DATA SPINE
 *     row 4   Raw Data Store
 *
 * Three things about v2 are load-bearing and easy to undo by accident:
 *
 *   1. **No plane bands are drawn.** The two background rects and their
 *      captions are gone. The :prop:`TopologyNode.plane` *attribute* survives —
 *      components still belong to a plane, the BFF still computes a plane
 *      rollup, and the UI surfaces it **textually** (Overall summary + each
 *      component's detail sub-heading). Only the visual containers went.
 *   2. **Ingress and Egress are ordinary fixed-size components** (128 × 64, the
 *      same rectangle as Time Series Manager). Their height does **not** grow
 *      with their interface roster (REQ-STATUS-036 amended 2026-08-01); the
 *      stack is free to be taller than its parent and extends symmetrically
 *      above and below it. A growing roster therefore costs stack height, not
 *      node height, and **moves no edge**.
 *   3. **They are centre-anchored.** Both declare ``cy`` and derive ``y``
 *      (:func:`resolveNode`). The arithmetic is trivial now that ``h`` is a
 *      constant, but the *datum* is what matters: every edge landing on the
 *      spine and both interface stacks are positioned from that centre, so
 *      declaring ``y`` instead would let the next height change quietly drag
 *      the spine off its own centre line.
 *
 * # Composition is unconditional (retires GAP-45(i))
 *
 * v2 composes **exactly one Provider Manager node and one Peer Manager node on
 * every network** (REQ-STATUS-025 amended 2026-07-31). Which ingress *kind* backs
 * the Provider Manager node (``provider_manager`` + ``provider``, on every network
 * since Phase I folded the CDS receive path into the provider chassis) is a
 * property of the reported data, not of the drawing — hence
 * :prop:`TopologyNode.kinds` is a *set* and :func:`composeNodes` takes no
 * arguments at all. The old network-variant filter is gone: composition can no
 * longer branch on the network because it can no longer see it, which is a
 * stronger form of the REQ-XSEC-007 guarantee than the filter it replaces.
 *
 * The ``collector`` and ``ui`` kinds are reported (or reserved) but are **not**
 * display nodes — storyboard §2 names neither. They still count in the
 * server-computed plane / overall rollup.
 *
 * # Edges are node-anchored, not coordinates (feature-spec §2.1)
 *
 * Node coordinates stay literal — the diagram is a deliberate hand-composed
 * picture, not a graph-layout problem (storyboard OQ-2) — but they are the
 * **single source from which edge geometry is derived**. An edge names its two
 * endpoint nodes and the side of each it leaves from (:type:`TopologyEdge`);
 * :func:`routeEdge` turns that into a polyline. Four properties fall out:
 *
 *   1. **Orthogonal by construction** — the router emits axis-aligned segments
 *      only, so a diagonal cannot be drawn.
 *   2. **At most one elbow by construction** — :prop:`TopologyEdge.elbow` is a
 *      single optional discriminator, so an edge wanting two turns is
 *      unrepresentable and has to be surfaced rather than quietly drawn
 *      (REQ-STATUS-045; architect ruling 3). The Phase-B transitional
 *      ``legacyDoubleElbowVia`` escape hatch is **deleted** here, along with
 *      the two S-bends that needed it: v2 places Tasking Manager directly above
 *      the Message Bus, so that route is now a straight vertical, and the
 *      ``BFF → Message Bus`` edge is not drawn at all (mock §4 g).
 *   3. **Edges cannot orphan** — endpoints are :type:`NodeId`s derived from
 *      :data:`TOPOLOGY_NODES`, so deleting a node is a *type* error at every
 *      edge that referenced it.
 *   4. **Moving a node is no longer an edge edit.**
 *
 * # Divergences from the requirement text, deliberately surfaced
 *
 * Two numbers here follow the mock (priority source, re-authored after the
 * requirement was written) rather than the REQ text, and both are one-line
 * flips behind named constants:
 *
 *   - :data:`INTERFACE_LABEL_MAX_CHARS` is **16 + a one-glyph `…`** (mock §3a),
 *     derived from the icon width; REQ-STATUS-037's original 10 + a literal
 *     three-character ``...`` is amended 2026-08-01 to match.
 *   - At overflow, :data:`INTERFACE_ICON_CAP` icons are drawn and the ``+K more``
 *     affordance sits BELOW them — 11 rows at a cap of 10 (architect, 2026-08-01;
 *     REQ-STATUS-040 as written). The mock's "the tenth slot itself becomes
 *     ``+K more``" is superseded: it would silently make the cap 9 at overflow.
 *
 * Cross-references:
 *   - Flow L §3 (roll-up view) / §2 (functional naming) / §11 (unknown).
 *   - docs/.../L-system-status/index.html (the coordinate source of record).
 *   - ADR-035 D9 (functional names); REQ-STATUS-016 (never a false green).
 */
import {
  ROLLUP_STATES,
  worstRollupState,
  type ComponentKind,
  type PlaneToken,
  type RollupState,
} from "@/system-status/status-model";

/**
 * The **base** diagram viewBox (the storyboard mock's). :func:`composeExtent`
 * treats it as a floor — the drawing grows below it when a deployment composes
 * a stack taller than the mock drew, and is byte-identical to it otherwise.
 */
export const DIAGRAM_VIEWBOX = { width: 1070, height: 562 } as const;

/**
 * The margin held on all four sides of the drawing (mock §1: the bounding box
 * is x 24…1046, y 24…538). :func:`composeExtent` re-applies it below whatever
 * composes; it never grows the drawing **sideways**, so both horizontal extremes
 * sit exactly ON this margin by construction rather than with slack behind them.
 *
 * That makes the margin a real constraint on two constants: widening
 * :data:`INTERFACE_ICON` or moving the Ingress / Egress nodes means re-deriving
 * :data:`DIAGRAM_VIEWBOX`'s width in the same edit. When the icon went 92 → 115
 * on 2026-08-01 the viewBox went 1024 → 1070 and every node translated +23 — a
 * uniform shift, so every alignment in the node table (all relative) survived
 * untouched. ``status-topology.test.ts`` asserts this margin against the
 * constants rather than against literals, so the next such change fails loudly
 * instead of silently rendering a clipped focus ring.
 */
export const DIAGRAM_MARGIN = 24;

// ---------------------------------------------------------------------------
// Nodes
// ---------------------------------------------------------------------------

/** The side of a node's rectangle an edge leaves from or arrives at. */
export type Anchor = "top" | "right" | "bottom" | "left";

/**
 * The side of a node an interface stack hangs off.
 *
 * Deliberately narrower than :type:`Anchor` (the feature spec's original
 * ``interfaceEdge?: Anchor``): the stack geometry is horizontal-only, so a
 * ``top`` or ``bottom`` value has no rendering and would be silently drawn as
 * ``right``. Making it unrepresentable is cheaper than a runtime guard.
 */
export type InterfaceEdge = Extract<Anchor, "left" | "right">;

/** Fields every node declares, however it is vertically anchored. */
interface TopologyNodeBase {
  /** Stable node id (also the SVG ``data-id`` + focus target). */
  id: string;
  /** Functional display name (storyboard §2; ADR-035 D9). */
  name: string;
  plane: PlaneToken;
  x: number;
  w: number;
  h: number;
  /** ``cyl`` for the object/data stores (MinIO, Postgres); ``box`` otherwise. */
  shape: "box" | "cyl";
  /**
   * The component kinds whose rollup drives this node's stoplight — a **set**,
   * because v2 draws one Ingress node backed by whichever ingress kind the
   * deployment reports and one Peer Manager node covering all three egress kinds
   * (REQ-STATUS-025 amended). Empty for a planned placeholder.
   */
  kinds: readonly ComponentKind[];
  /** Grayed placeholder for a future capability (REQ-STATUS-028). */
  planned: boolean;
  /**
   * Which edge of this node hosts its interface-icon stack, or absent when the
   * node hosts none (feature-spec §2.2). Only Ingress (``left``) and Egress
   * (``right``) host icons.
   */
  interfaceEdge?: InterfaceEdge;
}

/**
 * An authored node row: it declares **either** its top edge (``y``) **or** its
 * centre line (``cy``), never both and never neither. The exclusivity is a type
 * error rather than a review note, because the two data-spine nodes are
 * centre-anchored on purpose (see the module docstring, point 3) and a row that
 * quietly declared both would let the two datums drift apart.
 */
type TopologyNodeSpec = TopologyNodeBase &
  ({ y: number; cy?: undefined } | { cy: number; y?: undefined });

/**
 * A node with both vertical datums resolved. Consumers read ``y`` and ``cy``
 * interchangeably; :prop:`centreAnchored` records which one was authored, so a
 * renderer or a test can assert that the spine is still centre-driven.
 */
export interface TopologyNode extends TopologyNodeBase {
  /** Top edge, in diagram coordinates. */
  y: number;
  /** Centre line, in diagram coordinates. */
  cy: number;
  /** True when ``cy`` was the authored datum and ``y`` was derived from it. */
  centreAnchored: boolean;
}

/**
 * The node table, declared ``as const`` purely so :type:`NodeId` can be
 * *derived* from it rather than restated: removing an entry here turns every
 * edge that named it into a compile error (feature-spec §2.1 point 3).
 *
 * Every coordinate is the mock header §1 geometry table verbatim. The
 * alignments it calls "hard constraints, not preferences" (three right edges at
 * 725; Tasking Manager and Message Bus sharing a left edge; Egress and
 * Processed Data Store sharing a left edge; Ingress over Raw Data Store; the
 * spine on one centre line; 42-unit runs between neighbours) are asserted in
 * the test suite, not merely commented here.
 */
const NODE_TABLE = [
  // ---- row 1 (y 56) --------------------------------------------------------
  {
    id: "user-interface-manager",
    name: "User Interface Manager",
    plane: "control",
    x: 565,
    y: 24,
    w: 160,
    h: 64,
    shape: "box",
    kinds: ["bff"],
    planned: false,
  },
  // ---- row 2 (y 190) -------------------------------------------------------
  {
    id: "tasking-manager",
    name: "Tasking Manager",
    plane: "control",
    x: 345,
    y: 158,
    w: 128,
    h: 64,
    shape: "box",
    // The only remaining placeholder (REQ-STATUS-028 amended): sensor tasking
    // is deferred to ADR-037 §D-8 and reports no component of any kind.
    kinds: [],
    planned: true,
  },
  {
    id: "time-series-manager",
    name: "Time Series Manager",
    plane: "data",
    x: 597,
    y: 158,
    w: 128,
    h: 64,
    shape: "box",
    kinds: ["time_series"],
    planned: false,
  },
  {
    id: "processed-data-store",
    name: "Processed Data Store",
    plane: "data",
    x: 767,
    y: 158,
    w: 136,
    h: 64,
    shape: "cyl",
    kinds: ["data_store"],
    planned: false,
  },
  // ---- row 3 (cy 356) — the data spine ------------------------------------
  {
    id: "provider-manager",
    name: "Provider Manager",
    plane: "data",
    x: 175,
    // Centre-anchored: `cy` is the datum, `y` is derived. See module docstring.
    cy: 356,
    w: 128,
    h: 64,
    shape: "box",
    // The node's backing kinds. `provider_manager` is the service's own report and
    // `provider` serves BOTH the per-provider reports (`component_id`
    // "provider:<name>") and the per-interface trend reports (the bare interface
    // name) — the D-16 prefix discrimination, which is what keeps the two report
    // classes apart without a second kind.
    //
    // Both bespoke ingest kinds are GONE — `sapient_ingress` at Phase G and
    // `cds_ingress` at Phase I, each reserved in the proto with its stored tokens
    // rewritten onto `provider_manager` (migrations 067 and 070). The node is now
    // backed by the same two kinds on every network, and the network shape still
    // falls out of WHAT the snapshot reports (which providers exist) rather than a
    // `network === "u"` branch (REQ-XSEC-007).
    kinds: ["provider_manager", "provider"],
    planned: false,
    interfaceEdge: "left",
  },
  {
    id: "message-bus",
    name: "Message Bus",
    plane: "data",
    x: 345,
    y: 324,
    // 380 wide is not a taste call: it is what makes the Message Bus's right
    // edge coincide with User Interface Manager's and Time Series Manager's at
    // 725 while its left edge coincides with Tasking Manager's at 345.
    w: 380,
    h: 64,
    shape: "box",
    kinds: ["message_bus"],
    planned: false,
  },
  {
    id: "peer-manager",
    name: "Peer Manager",
    plane: "data",
    x: 767,
    cy: 356,
    w: 128,
    h: 64,
    shape: "box",
    // The service's own report plus the two per-interface egress kinds roll up into
    // the one node. `peer_cds` was removed 2026-08-11 (§2.9.1, S-7): it named this
    // service's own report under a CDS-shaped kind, and `peer_manager` — which
    // already existed and nothing emitted — names it for what it is.
    kinds: ["peer_manager", "peer_sapient", "peer_raw"],
    planned: false,
    interfaceEdge: "right",
  },
  // ---- row 4 (y 508) -------------------------------------------------------
  {
    id: "raw-data-store",
    name: "Raw Data Store",
    plane: "data",
    x: 185,
    y: 478,
    w: 108,
    h: 60,
    shape: "cyl",
    kinds: ["raw_data_store"],
    planned: false,
  },
] as const satisfies readonly TopologyNodeSpec[];

/** Resolve an authored row's second vertical datum from its first. */
function resolveNode(spec: TopologyNodeSpec): TopologyNode {
  const anchored =
    spec.cy === undefined
      ? { y: spec.y, cy: spec.y + spec.h / 2, centreAnchored: false }
      : { y: spec.cy - spec.h / 2, cy: spec.cy, centreAnchored: true };
  return { ...spec, ...anchored };
}

/**
 * The composed node list. Coordinates are the storyboard mock's verbatim, with
 * ``y``/``cy`` reconciled by :func:`resolveNode`.
 */
export const TOPOLOGY_NODES: readonly TopologyNode[] = NODE_TABLE.map(resolveNode);

/**
 * Every id a topology node can have, derived from the node table so the two can
 * never disagree. Edges are declared against this union: delete a node and its
 * edges stop compiling (feature-spec §2.1 point 3).
 */
export type NodeId = (typeof NODE_TABLE)[number]["id"];

/**
 * The nodes a deployment draws.
 *
 * v2 composes the same eight nodes everywhere (REQ-STATUS-025 amended
 * 2026-07-31), so this is deliberately a **total** function of no arguments:
 * it cannot branch on the network because it is not given it, and the Egress
 * node cannot fail to compose for a deployment that has configured nothing yet
 * (REQ-STATUS-041 — the egress-management affordance hangs off this node and is
 * otherwise unreachable, with no SSH access to the VMs).
 */
export function composeNodes(): readonly TopologyNode[] {
  return TOPOLOGY_NODES;
}

// ---------------------------------------------------------------------------
// Identity — Process and Technology (REQ-STATUS-027 amended 2026-07-31)
// ---------------------------------------------------------------------------

/**
 * The Meridian **process** behind a component kind, where one exists.
 *
 * Authored here as UI literals: ``ComponentModel`` carries no process-name
 * field and none exists anywhere in telemetry (REQ-STATUS-027 Notes), so this
 * table and :data:`TECHNOLOGY_BY_KIND` are the whole source. They are keyed by
 * *kind* rather than by node so the one Ingress node can name the process the
 * deployment is actually running without a network branch.
 *
 * There is deliberately **no ``meridian_sapient_egress``**: every egress kind,
 * CDS included, is served by the one ``meridian_peer_manager`` process.
 */
const PROCESS_BY_KIND: Partial<Record<ComponentKind, string>> = {
  bff: "meridian_bff",
  time_series: "meridian_time_series",
  provider_manager: "meridian_provider_manager",
  provider: "meridian_provider_manager",
  peer_manager: "meridian_peer_manager",
  peer_sapient: "meridian_peer_manager",
  peer_raw: "meridian_peer_manager",
  collector: "meridian_status_collector",
};

/**
 * The COTS **technology** behind a component kind, where one exists.
 *
 * Distinct from :data:`PROCESS_BY_KIND` on purpose — ``MinIO`` is not a process
 * name and ``meridian_bff`` is not a technology, so folding them into one field
 * would force a lie on one node or the other (REQ-STATUS-027 Notes).
 */
const TECHNOLOGY_BY_KIND: Partial<Record<ComponentKind, string>> = {
  message_bus: "NATS JetStream",
  data_store: "PostgreSQL + TimescaleDB",
  raw_data_store: "MinIO",
};

/**
 * Whether a component kind is a **COTS product** rather than a Meridian service.
 *
 * Derived from :data:`TECHNOLOGY_BY_KIND` rather than restated, because "has a
 * technology" and "is COTS" are the same fact and a second list would drift the
 * first time a product is added.
 *
 * Load-bearing for the resource rows: a COTS component does not run
 * ``meridian_status``. The collector observes it from outside through the
 * product's own admin API, which yields its domain metrics but can never yield
 * another process's cgroup — so cpu / memory / fd / event-loop-lag are
 * structurally absent, not missing (REQ-STATUS-008).
 */
export function isCotsKind(kind: ComponentKind | string): boolean {
  return TECHNOLOGY_BY_KIND[kind as ComponentKind] !== undefined;
}

/**
 * A node's Identity group — the two strings v2 strips off the canvas
 * (REQ-STATUS-046) and re-homes in Selection Detail.
 *
 * Both are nullable and **rendered only where known**: a node that has no
 * process (a COTS store) or no technology (a Meridian service) shows the field
 * not at all rather than showing an em dash that reads as data.
 */
export interface NodeIdentity {
  /** e.g. ``meridian_bff``; null where the node is not a Meridian process. */
  process: string | null;
  /** e.g. ``MinIO``; null where the node is not a COTS product. */
  technology: string | null;
}

/**
 * The one value shared by every mapped kind, or null when there is none or when
 * the kinds disagree. Disagreement is the interesting case: the Ingress node's
 * two candidate kinds name two different processes, so with nothing reported
 * there is genuinely nothing to say and null is the honest answer.
 */
function soleValue(values: readonly (string | undefined)[]): string | null {
  const distinct = new Set(values.filter((v): v is string => v !== undefined));
  return distinct.size === 1 ? [...distinct][0] : null;
}

/**
 * Resolve one identity field for a node: prefer the kinds the snapshot actually
 * reports, and fall back to the node's declared kinds when they all agree
 * anyway (which is what lets a structural, zero-interface Peer Manager node still
 * name ``meridian_peer_manager``).
 */
function resolveIdentityField(
  node: TopologyNode,
  reportedKinds: ReadonlySet<ComponentKind>,
  table: Partial<Record<ComponentKind, string>>,
): string | null {
  const reported = node.kinds.filter((kind) => reportedKinds.has(kind));
  return (
    soleValue(reported.map((kind) => table[kind])) ??
    soleValue(node.kinds.map((kind) => table[kind]))
  );
}

/**
 * The **Process** and **Technology** fields for a node's Selection Detail
 * Identity group (REQ-STATUS-027 amended 2026-07-31).
 *
 * ``reportedKinds`` is what makes the single Provider Manager node honest across
 * networks — it names the processes the snapshot actually reports, derived from the
 * data rather than from a ``network`` branch (REQ-XSEC-007). Since Phase I that is
 * ``meridian_provider_manager`` on every network, the CDS receive path included.
 */
export function nodeIdentity(
  node: TopologyNode,
  reportedKinds: ReadonlySet<ComponentKind>,
): NodeIdentity {
  return {
    process: resolveIdentityField(node, reportedKinds, PROCESS_BY_KIND),
    technology: resolveIdentityField(node, reportedKinds, TECHNOLOGY_BY_KIND),
  };
}

// ---------------------------------------------------------------------------
// Edges
// ---------------------------------------------------------------------------

/**
 * The world outside the drawing — sensors upstream of Ingress, Consumers
 * downstream of Egress. Not a node (it has no rectangle, no status and no
 * selection), so an edge that touches it terminates on a short stub extending
 * outward from the *other* endpoint's anchor.
 *
 * **v2 declares no such edge**: the interface stacks now occupy the drawing's
 * left and right extremes (x 24 and x 1046), leaving no room for an off-diagram
 * stub. The router support is retained because it is the Phase-B edge model and
 * the sensors / consumers relationship is still real — it is carried in each
 * node's Selection Detail roster rather than on the canvas.
 */
export const EXTERNAL = "external";

/** An edge terminal: a composed node, or the off-diagram exterior. */
export type EdgeEndpoint = NodeId | typeof EXTERNAL;

/**
 * Stroke class. ``raw`` = the Topology-A raw-blob bypass (ADR-002 — the blob
 * never rides the bus); ``future`` = a planned, not-yet-built seam, drawn
 * dashed.
 *
 * v2 tags no edge ``raw``: the mock carries the bypass in *geometry* — the raw
 * path runs Ingress → Raw Data Store → Egress and never touches the Message Bus
 * — rather than in a second stroke colour. The member is retained because the
 * renderer branches on it and the distinction may be restored.
 */
export type EdgeKind = "raw" | "future";

/** A point in diagram (viewBox) coordinates. */
export interface Point {
  x: number;
  y: number;
}

/**
 * A **declarative** edge: which two nodes it joins and where on each it lands.
 * All geometry is derived by :func:`routeEdge` from the endpoints' composed
 * rectangles — this type carries no coordinate that describes the *route*.
 */
export interface TopologyEdge {
  /** Source node, or :data:`EXTERNAL` for an inbound off-diagram stub. */
  from: EdgeEndpoint;
  /**
   * Side of ``from`` the edge leaves. When ``from`` is :data:`EXTERNAL` this
   * names the side of the drawing the stub runs off toward, and must mirror
   * ``toAnchor`` — otherwise the stub would double back across the node.
   */
  fromAnchor: Anchor;
  /** Target node, or :data:`EXTERNAL` for an outbound off-diagram stub. */
  to: EdgeEndpoint;
  /** Side of ``to`` the edge arrives at (see ``fromAnchor`` for EXTERNAL). */
  toAnchor: Anchor;
  /**
   * Displacement along ``fromAnchor``'s edge from its midpoint, for landing an
   * edge somewhere other than the centre of a side. Still node-relative: moving
   * the node moves the offset with it.
   */
  fromOffset?: number;
  /** Displacement along ``toAnchor``'s edge from its midpoint. */
  toOffset?: number;
  /**
   * Standoff held between the arrow tip and the target rectangle, so the
   * arrowhead reads as pointing *at* the box rather than sitting on its
   * stroke. Applied at the ``to`` end only.
   */
  arrowGap?: number;
  /** Length of the off-diagram stub when one endpoint is :data:`EXTERNAL`. */
  stubLength?: number;
  /**
   * Which axis the single permitted elbow turns on. Omit for a straight run.
   * There is deliberately no way to ask for a second elbow: an edge needing two
   * turns must be surfaced to the architect (REQ-STATUS-045; ruling 3).
   */
  elbow?: "horizontal-first" | "vertical-first";
  kind?: EdgeKind;
  /** Draws a reverse arrowhead as well (for a two-way link). */
  bidirectional?: boolean;
}

/** A composed edge with its geometry resolved (:func:`routeEdge`). */
export interface RoutedEdge extends TopologyEdge {
  /**
   * The route as a polyline, in draw order: 2 points for a straight run, 3 for
   * a single elbow. Every consecutive pair shares an axis.
   */
  points: readonly Point[];
}

/**
 * Half the Message Bus's width less the run to its neighbours' centre lines.
 *
 * The Message Bus is 380 wide and centred at x 512, so ±126 along its top edge
 * lands exactly on Tasking Manager's centre (386) and Time Series Manager's
 * centre (638). Both verticals are therefore straight — zero elbows — at both
 * ends. **Change the Message Bus width and this must be re-derived**, which is
 * why it is one named constant used twice rather than two literals.
 */
const BUS_BRANCH_OFFSET = 126;

/**
 * The nine v2 edges, transcribed from mock header §2 in table order.
 *
 * Every one is orthogonal with at most one elbow, no edge carries a label, none
 * crosses another, and none passes through a rectangle. The two long runs are
 * the read path (x 812, right of everything in rows 1–2) and the cold-read path
 * (y 508, below everything in row 3).
 *
 * Two edges the previous topology drew are **gone by ruling**, not by
 * oversight: ``User Interface Manager → Message Bus`` (mock §4 g — the UIM's
 * centre x falls inside Time Series Manager's span, so any descent needs a
 * second elbow or clips that node; the SSE fan-out relationship survives in
 * both nodes' Selection Detail rosters), and every off-diagram sensors /
 * consumers stub (see :data:`EXTERNAL`).
 */
export const TOPOLOGY_EDGES: readonly TopologyEdge[] = [
  // ---- the data spine — three nodes on one centre line, both runs straight --
  { from: "provider-manager", fromAnchor: "right", to: "message-bus", toAnchor: "left" },
  { from: "message-bus", fromAnchor: "right", to: "peer-manager", toAnchor: "left" },
  // ---- raw path (ADR-002 Topology A) — drawn end to end, both directions ----
  // The blob bypasses the bus on the way in and on the way out (REQ-STATUS-024);
  // `arrowGap` stands the arrowhead off the cylinder's top ellipse, and the
  // return leg lands on Egress's BOTTOM edge because its left is already taken.
  {
    from: "provider-manager",
    fromAnchor: "bottom",
    to: "raw-data-store",
    toAnchor: "top",
    arrowGap: 9,
  },
  {
    from: "raw-data-store",
    fromAnchor: "right",
    to: "peer-manager",
    toAnchor: "bottom",
    elbow: "horizontal-first",
  },
  // ---- processed path ------------------------------------------------------
  // REVERSED in v2: the arrowhead points AT Time Series Manager, straight up
  // from the bus's top edge into that node's bottom edge.
  {
    from: "message-bus",
    fromAnchor: "top",
    fromOffset: BUS_BRANCH_OFFSET,
    to: "time-series-manager",
    toAnchor: "bottom",
  },
  {
    from: "time-series-manager",
    fromAnchor: "right",
    to: "processed-data-store",
    toAnchor: "left",
  },
  // ---- read path — up the right-hand margin, then left into the UI manager --
  {
    from: "processed-data-store",
    fromAnchor: "top",
    to: "user-interface-manager",
    toAnchor: "right",
    elbow: "vertical-first",
  },
  // ---- tasking seam — DASHED at both hops ---------------------------------
  // Tasking Manager is a planned capability (REQ-STATUS-028), so neither hop
  // may render like a live one.
  {
    from: "user-interface-manager",
    fromAnchor: "left",
    to: "tasking-manager",
    toAnchor: "top",
    elbow: "horizontal-first",
    kind: "future",
  },
  {
    from: "tasking-manager",
    fromAnchor: "bottom",
    to: "message-bus",
    toAnchor: "top",
    toOffset: -BUS_BRANCH_OFFSET,
    kind: "future",
  },
];

/** Unit normal pointing away from the node, per anchor side. */
const OUTWARD: Readonly<Record<Anchor, Point>> = {
  top: { x: 0, y: -1 },
  right: { x: 1, y: 0 },
  bottom: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
};

/** Default off-diagram stub length when an edge does not name one. */
const EXTERNAL_STUB_LENGTH = 28;

/**
 * The point where an edge meets a node: the midpoint of the named side,
 * displaced along that side by ``offset`` (positive = right / down).
 */
function anchorPoint(node: TopologyNode, anchor: Anchor, offset: number): Point {
  const cx = node.x + node.w / 2;
  switch (anchor) {
    case "top":
      return { x: cx + offset, y: node.y };
    case "bottom":
      return { x: cx + offset, y: node.y + node.h };
    case "left":
      return { x: node.x, y: node.cy + offset };
    case "right":
      return { x: node.x + node.w, y: node.cy + offset };
  }
}

/** Step ``distance`` away from the node, perpendicular to the anchored side. */
function outward(from: Point, anchor: Anchor, distance: number): Point {
  const normal = OUTWARD[anchor];
  return { x: from.x + normal.x * distance, y: from.y + normal.y * distance };
}

/**
 * Resolve one declarative edge into a polyline against a composed node set.
 *
 * Returns ``null`` when either endpoint did not compose — the anti-orphan rule
 * (feature-spec §2.1 point 3). Dropping the edge is the fail-safe reading: an
 * arrow drawn to a vacated coordinate asserts a data flow that is not on the
 * display, which CLAUDE.md §8.1 (operator visibility, never a misleading
 * picture) forbids more strongly than a missing decoration.
 *
 * The route is orthogonal by construction: a straight run when ``elbow`` is
 * omitted, otherwise one turn on the named axis. There is no code path that can
 * emit a diagonal, and none that can emit two turns.
 *
 * An edge touching :data:`EXTERNAL` is always a straight collinear stub — there
 * is no second rectangle to turn toward, so ``elbow`` has no meaning on one.
 */
export function routeEdge(
  edge: TopologyEdge,
  nodesById: ReadonlyMap<string, TopologyNode>,
): RoutedEdge | null {
  const fromNode = edge.from === EXTERNAL ? null : (nodesById.get(edge.from) ?? null);
  const toNode = edge.to === EXTERNAL ? null : (nodesById.get(edge.to) ?? null);
  if (edge.from !== EXTERNAL && fromNode === null) return null;
  if (edge.to !== EXTERNAL && toNode === null) return null;

  const stub = edge.stubLength ?? EXTERNAL_STUB_LENGTH;
  let start: Point;
  let end: Point;
  if (toNode === null) {
    // Outbound stub: run off the drawing from the source node's anchor.
    if (fromNode === null) return null; // both ends external — nothing to anchor to.
    start = anchorPoint(fromNode, edge.fromAnchor, edge.fromOffset ?? 0);
    end = outward(start, edge.fromAnchor, stub);
  } else {
    const landing = anchorPoint(toNode, edge.toAnchor, edge.toOffset ?? 0);
    end = outward(landing, edge.toAnchor, edge.arrowGap ?? 0);
    start =
      fromNode === null
        ? // Inbound stub: the sensors/consumers world, ``stub`` out from the box.
          outward(landing, edge.toAnchor, stub)
        : anchorPoint(fromNode, edge.fromAnchor, edge.fromOffset ?? 0);
  }

  const points =
    fromNode === null || toNode === null ? [start, end] : routePoints(start, end, edge.elbow);
  return { ...edge, points: dedupe(points) };
}

/** The corner sequence between two anchor points, per the declared elbow. */
function routePoints(start: Point, end: Point, elbow: TopologyEdge["elbow"]): Point[] {
  if (elbow === undefined) return [start, end];
  return elbow === "vertical-first"
    ? [start, { x: start.x, y: end.y }, end]
    : [start, { x: end.x, y: start.y }, end];
}

/** Drop corners that coincide with their predecessor (a degenerate elbow). */
function dedupe(points: readonly Point[]): Point[] {
  return points.filter((p, i) => i === 0 || p.x !== points[i - 1].x || p.y !== points[i - 1].y);
}

/**
 * The SVG ``d`` for a routed polyline, using the ``V``/``H`` shorthand an
 * orthogonal route allows.
 *
 * The ``L`` branch is unreachable from :func:`routeEdge` (every segment shares
 * an axis with its predecessor by construction) and exists so that a
 * non-orthogonal run would be *written as a diagonal* rather than silently
 * rounded onto an axis — the failure ruling 3 exists to prevent.
 */
export function edgePathD(points: readonly Point[]): string {
  return points
    .map((p, i) => {
      if (i === 0) return `M${p.x},${p.y}`;
      const prev = points[i - 1];
      if (p.x === prev.x) return ` V${p.y}`;
      if (p.y === prev.y) return ` H${p.x}`;
      return ` L${p.x},${p.y}`;
    })
    .join("");
}

/**
 * Compose the edges and resolve their geometry.
 *
 * Every v2 node composes unconditionally, so the only filter left is the
 * anti-orphan one inside :func:`routeEdge` — an edge can never outlive the node
 * it points at. Declaration order is preserved, so the SVG document order is
 * stable across deployments.
 */
export function composeEdges(): RoutedEdge[] {
  const nodesById = new Map<string, TopologyNode>(composeNodes().map((node) => [node.id, node]));
  const routed: RoutedEdge[] = [];
  for (const edge of TOPOLOGY_EDGES) {
    const route = routeEdge(edge, nodesById);
    if (route !== null) routed.push(route);
  }
  return routed;
}

// ---------------------------------------------------------------------------
// Interface icons (REQ-STATUS-035..-041)
// ---------------------------------------------------------------------------

/**
 * Icon-box geometry — one shared constant, so every icon on every node is
 * identical in width, height and spacing (REQ-STATUS-036). Mock §3 values.
 */
export const INTERFACE_ICON = { width: 115, height: 18, gap: 4 } as const;

/** Vertical distance between successive icon tops (height + gap). */
export const INTERFACE_ICON_PITCH = INTERFACE_ICON.height + INTERFACE_ICON.gap;

/**
 * Gap held between the stack and the separator rule, and again between the rule
 * and the parent (mock §3). Sized against the **focus ring**, not taste: a
 * focused Ingress/Egress ring stands 11 units off the body, so it clears the
 * rule by 7. Shrink this below 12 and focus eats the separator.
 */
export const INTERFACE_STACK_SEP_GAP = 18;

/**
 * The number of **icons** a node's stack will draw (REQ-STATUS-040).
 *
 * Icons, not slots: where the roster overflows, the ``+K more`` affordance is an
 * **additional row below** them, so an overflowing stack is ``CAP + 1`` rows —
 * 31 at a cap of 30. Reading this as a total slot count would silently make the
 * cap 29 the moment overflow occurred.
 *
 * # Why 30, and what bounds it
 *
 * Raised 10 → 30 on 2026-08-01 (architect): a laptop-sized panel has the room,
 * and ADR-037 targets ≥20 ingress interfaces. The bound is **vertical**, and it
 * is tighter than it looks because the stack is centre-anchored on its parent
 * (y 356) and grows *both* ways, while :func:`composeExtent` grows the viewBox
 * **downward only**:
 *
 * .. code-block:: text
 *
 *     30 icons          → 656 tall, top  28  (clears the 24-unit margin by 4)
 *     30 icons + '+K'   → 678 tall, top  17  (inside the margin, not clipped)
 *     32 icons + '+K'   → 722 tall, top  -5  (CLIPPED — off the top of the canvas)
 *
 * So 30 is close to the ceiling, not comfortably below it, and the *top* is what
 * runs out first. :func:`composeExtent` cannot rescue a negative top by growing,
 * because growing adds height at the bottom. ``status-topology.test.ts`` asserts
 * the top margin at both the cap and cap + 1 so a further raise fails loudly.
 *
 * ⚠ Raising this makes the diagram **taller on screen, not denser**: the SVG is
 * ``w-full h-auto`` with ``preserveAspectRatio="xMidYMid meet"``, so render scale
 * is ``panelWidth / viewBox.width`` and is independent of height. Nothing shrinks
 * to fit; a composition taller than the panel scrolls (REQ-STATUS-047).
 */
export const INTERFACE_ICON_CAP = 30;

/**
 * Character budget for an icon's visible label (mock §3a).
 *
 * **Derived from :data:`INTERFACE_ICON`'s width — not chosen beside it.** These
 * two constants are one decision and must move together; that they sat 34 lines
 * apart with nothing saying so is why widening the box on 2026-08-01 initially
 * changed nothing an operator could see. The arithmetic, matching the mock:
 *
 * .. code-block:: text
 *
 *     run = width − INTERFACE_LABEL_INSET = 115 − 22 = 93 units
 *     93 ÷ 5.4 (0.6 em at fontSize 9) = 17 glyphs = 16 characters + '…'
 *
 * Run backwards on the previous 92-unit box it yields (92 − 22) ÷ 5.4 = 12.96
 * ⇒ 13 glyphs = the 12 this used to be, which is where that number came from.
 *
 * The 0.6 em figure holds **only because the label renders in the monospace
 * token** (REQ-STATUS-037, applied to the ``<text>`` in ``status-diagram.tsx``).
 * In a proportional face a character count is not a width — the widest 16
 * glyphs and the narrowest differ by more than the box's slack — and SVG text
 * does not clip, so an over-budget label would simply spill out of its box.
 * Changing the font invalidates this budget.
 *
 * ⚠ This does not end truncation and cannot: interface names are bounded at 128
 * characters, which would need ~713 units of run. It buys 12 → 16.
 */
export const INTERFACE_LABEL_MAX_CHARS = 16;

/** The glyph appended to a truncated label. See the budget note above. */
export const INTERFACE_LABEL_ELLIPSIS = "…";

/**
 * The peer interface **types** that do not *yet* report a per-interface status
 * component — a temporary backend gap, not a display rule.
 *
 * ``meridian_peer_manager`` emits one ``ComponentStatusReport`` per configured
 * *direct* interface (``sapient-data`` / ``raw-data``) and none for the CDS
 * types. The rationale previously recorded here — that the ``peer-cds``
 * component id, kind and metric family were "accreditation-relevant and not
 * generalized" — was **retracted by the architect on 2026-08-01**: a CDS Egress
 * is an ordinary pre-configured peer interface and *should* report like one.
 * Closing that gap is issue #754.
 *
 * Nothing in this module branches on this set. It is exported so tests can name
 * the types whose icons legitimately read ``unknown`` until #754 lands.
 */
export const CDS_PEER_INTERFACE_TYPE_IDS: ReadonlySet<string> = new Set(["sapient-cds", "raw-cds"]);

/**
 * One configured interface, as the config roster reports it
 * (``GET /api/admin/peers/interfaces``).
 *
 * The config roster is authoritative for **existence** and the status feed for
 * **health** (feature-spec §2.2); ``name`` is the join key between them,
 * because a direct interface reports with ``component_id == config.name``.
 */
export interface ConfiguredInterface {
  /** Operator label, unique across interfaces; ≤128 characters. */
  name: string;
  /** Registered interface type (``sapient-data`` / ``raw-cds`` / …). */
  typeId: string;
  /**
   * The post-codec terminal — ``spooled`` or ``direct`` (REQ-PEER-079), or
   * ``null`` when this session was not sent it.
   *
   * ⚠ **Privileged**, unlike ``typeId`` beside it, so ``null`` is the ordinary
   * case for an `operator` / `analyst` session rather than an error. It drives
   * REQ-UI-149: a spooled interface surfaces spool depth and oldest-envelope
   * age, a direct one omits them rather than rendering zeroes, and a session
   * that cannot see the terminal is told so instead of getting either.
   */
  terminal?: string | null;
}

/**
 * The ``component_id`` namespace a **peer** reports under
 * (``PEER_COMPONENT_ID_PREFIX`` in ``meridian_peer_manager._interface_status``).
 *
 * ⚠ The namespace is load-bearing, not decoration. A peer and one of its own
 * interfaces may legitimately share a name — the seeded CDS peer is called
 * ``cds`` and so is an interface beneath it — while ``component_id`` is both this
 * display's identity and the status hypertable's key. Joining a peer roster on
 * the bare name would silently match an interface's samples and paint one
 * entity's health onto another.
 */
export const PEER_COMPONENT_ID_PREFIX = "peer:";

/** The ``component_id`` a peer named ``name`` reports under. */
export function peerComponentId(name: string): string {
  return `${PEER_COMPONENT_ID_PREFIX}${name}`;
}

/**
 * One configured peer, as the peer roster reports it (``GET /api/admin/peers``).
 *
 * ``interfaceCount`` is carried because it is what separates two peers that are
 * *both* absent from the status feed and only one of which is a fault — see
 * :func:`composePeerStack`. The list route serves it for every role
 * (REQ-UI-140), as ``0`` rather than absent for a peer that genuinely has none.
 */
export interface ConfiguredPeer {
  /** Operator label, unique across peers; the join key, minus the namespace. */
  name: string;
  /** How many interfaces it owns, or ``null`` when the read did not count them. */
  interfaceCount: number | null;
}

/**
 * Which level a node's icon stack enumerates.
 *
 * Ingress lists **interfaces**; the Peer Manager lists **peers**
 * (REQ-STATUS-081). The geometry is identical and deliberately shared; what
 * differs is the join rule feeding it and the selection a click originates, so
 * the level travels on the composed stack rather than being re-derived from a
 * node id at each use site.
 */
export type StackLevel = "interface" | "peer";

/**
 * Rollup states in worst-first order, **derived** from the same display
 * priority the node stoplight uses (:func:`worstRollupState`) rather than
 * restated. A change to that priority therefore reorders interface icons in
 * step, and the two can never drift.
 */
const WORST_FIRST_ORDER: readonly RollupState[] = [...ROLLUP_STATES].sort((a, b) =>
  a === b ? 0 : worstRollupState([a, b]) === a ? -1 : 1,
);

/**
 * A state's worst-first **display** rank; 0 is the most actionable.
 *
 * ⚠ Not the collector's rollup severity, and the two must not be confused. The
 * collector gives ``standby`` and ``stopped`` the same ordinal as ``healthy``
 * (0) precisely so a deliberately-idle child cannot raise its parent; this rank
 * orders what an operator should look at first, where an idle entity sorts
 * *ahead* of a running one. Rollup arithmetic uses the collector's ordinals
 * server-side; everything that merely puts rows or icons in an order uses this.
 *
 * Exported so every worst-first surface — the overflowing icon stack, the peer
 * pane's interface list — sorts by one rule that cannot drift.
 */
export function rollupDisplayRank(state: RollupState): number {
  return WORST_FIRST_ORDER.indexOf(state);
}

/**
 * Sort rollup states worst-first (unhealthy → degraded → unknown → healthy →
 * standby). Exported so the ordering rule is directly testable rather than only
 * observable through a composed stack.
 */
export function interfaceWorstFirst(states: readonly RollupState[]): RollupState[] {
  return [...states].sort((a, b) => rollupDisplayRank(a) - rollupDisplayRank(b));
}

/**
 * Truncate an interface name to the icon's character budget, measured in
 * **Unicode code points** rather than UTF-16 units so an astral character costs
 * one glyph rather than two (REQ-STATUS-037).
 *
 * Display-only: the untruncated name stays on the icon's accessible name, its
 * tooltip, its ``data-testid`` and its selection id, so no selector and no API
 * call ever depends on the budget.
 */
export function truncateInterfaceLabel(name: string): string {
  const points = [...name];
  if (points.length <= INTERFACE_LABEL_MAX_CHARS) return name;
  return points.slice(0, INTERFACE_LABEL_MAX_CHARS).join("") + INTERFACE_LABEL_ELLIPSIS;
}

/** One composed icon in a node's stack — an interface, or a peer. */
export interface InterfaceIcon {
  /** Full, untruncated entity name — the join key, selection id and a11y name. */
  name: string;
  /** The visible label, truncated to the icon's budget. */
  label: string;
  /**
   * Registered interface type, for the detail pane and the icon's a11y name.
   *
   * ``null`` on a **peer** icon, which has no type: a peer is a counterparty,
   * and it is its *interfaces* that carry a wire protocol. Nullable rather than
   * absent so the distinction is stated in the type instead of being a missing
   * property a caller might read as an oversight.
   */
  typeId: string | null;
  /**
   * Server-computed rollup for this interface, or ``unknown`` when the status
   * feed has not reported it yet (REQ-STATUS-038; never a false green).
   */
  state: RollupState;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The ``+K more`` affordance occupying the stack's last slot on overflow. */
export interface InterfaceOverflow {
  /** K — how many configured icon-bearing interfaces are **not** drawn. */
  count: number;
  /** The rendered text, e.g. ``+16 more``. */
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A node's composed icon stack (REQ-STATUS-036 amended 2026-08-01). */
export interface InterfaceStack {
  /** The hosting node's id. */
  parentId: string;
  /** Whether these icons are interfaces or peers (REQ-STATUS-081). */
  level: StackLevel;
  /** Which side of the parent the stack sits on (``left`` / ``right``). */
  side: InterfaceEdge;
  icons: readonly InterfaceIcon[];
  /** The overflow affordance, or null when the roster fits the cap. */
  overflow: InterfaceOverflow | null;
  /** Drawn slots = icons + (overflow ? 1 : 0). */
  slots: number;
  /** Shared left edge of every icon box. */
  x: number;
  /** Top of the first slot. Equals the parent's centre line when empty. */
  top: number;
  /** Total stack height; **0**, never negative, at zero slots. */
  height: number;
  /** ``top + height``. */
  bottom: number;
  /** x of the vertical separator rule between stack and parent. */
  separatorX: number;
  /** Top of the separator rule — it spans the stack, not the parent. */
  separatorTop: number;
  /** Bottom of the separator rule. */
  separatorBottom: number;
  /**
   * Configured interfaces this node hosts that compose **no** icon — today,
   * always empty since the 2026-08-01 ruling (CDS composes like any other). Returned
   * rather than dropped so the caller can list them in Selection Detail and
   * keep REQ-STATUS-042's representation invariant true.
   */
  excluded: readonly ConfiguredInterface[];
}

/**
 * Compose a node's interface stack: **existence from the configured roster,
 * health from a left join onto the status feed** (feature-spec §2.2).
 *
 * The left join is the point. A just-created interface is in the config roster
 * before its first status sample, so joining the other way round would make it
 * invisible until it reported — an operator would create an interface and see
 * nothing. Unjoined therefore means ``unknown`` ("not yet reporting"), never
 * absent and never green.
 *
 * Ordering follows the mock: the **configured order is preserved** while the
 * roster fits the cap (so the stack reads the way the operator's own list
 * does), and flips to **worst-state-first** on overflow — the one case where
 * ordering could hide a failing interface behind ten healthy ones, which is a
 * false green (CLAUDE.md §8.1; REQ-STATUS-016). The sort is stable, so equal
 * severities keep configured order even then.
 *
 * Geometry is centre-anchored on the parent's centre line, so a roster that
 * grows or shrinks expands the stack symmetrically and **moves nothing else** —
 * the parent rectangle is a fixed ordinary component size (REQ-STATUS-036
 * amended 2026-08-01) and every edge landing on it is unaffected.
 *
 * Returns ``null`` for a node that hosts no interfaces (no ``interfaceEdge``).
 */
export function composeInterfaceStack(
  node: TopologyNode,
  roster: readonly ConfiguredInterface[],
  statesByName: ReadonlyMap<string, RollupState>,
): InterfaceStack | null {
  // Every configured interface gets an icon. CDS is NOT special (architect, 2026-08-01):
  // a CDS Egress is an ordinary pre-configured peer interface. It renders today with an
  // `unknown` indicator — accurate, since it is configured and its health is not reported —
  // and resolves to real state with zero change here once #754 lands. That is why no
  // placeholder and no special case are needed: the left join already models both states.
  return composeStack(
    node,
    "interface",
    roster.map((iface) => ({
      name: iface.name,
      typeId: iface.typeId,
      // Left join: absent from the feed → honestly unknown (REQ-STATUS-016).
      state: statesByName.get(iface.name) ?? ("unknown" as RollupState),
    })),
  );
}

/**
 * Compose the Peer Manager's **peer** stack (REQ-STATUS-081) — the same
 * existence-from-config, health-from-feed rule one level up.
 *
 * # The join key is namespaced, and that is not cosmetic
 *
 * A peer reports under ``peer:<name>`` (:func:`peerComponentId`) because a peer
 * and one of its own interfaces may share a name. Joining on the bare name would
 * match the interface's samples and paint one entity's health onto another —
 * silently, and most likely on the seeded CDS peer, which is exactly where a
 * wrong reading is least affordable.
 *
 * # Two ways to be absent from the feed, and only one of them is a fault
 *
 * ``meridian_peer_manager`` groups its reports by peer over the *resolved
 * interfaces*, so a peer with **no interfaces emits no report at all** — verified
 * on the live testbed, where such a peer has no row in ``status.component_samples``
 * after 35 minutes of a ~10 s cadence. That makes the collector's own
 * ``peer_rollup(empty) → unhealthy`` branch unreachable and leaves this join as
 * the only place the case is decided. It is decided the same way the collector
 * would have, so the two levels agree rather than describing one peer
 * differently:
 *
 * - **no interfaces configured** → ``unhealthy``. Not uncertainty: a peer
 *   configured to move data with no path to move it on is the same fault
 *   REQ-STATUS-087 names one level down.
 * - **has interfaces, no sample yet** → ``unknown``. A peer created seconds ago
 *   is genuinely not yet reporting, and must be visible while that is true —
 *   never absent, never green (REQ-STATUS-016).
 *
 * ``interfaceCount === null`` (a read that did not count) falls to ``unknown``:
 * the fault reading is only claimed on a *known* zero.
 */
export function composePeerStack(
  node: TopologyNode,
  roster: readonly ConfiguredPeer[],
  statesById: ReadonlyMap<string, RollupState>,
): InterfaceStack | null {
  return composeStack(
    node,
    "peer",
    roster.map((peer) => ({
      name: peer.name,
      typeId: null,
      state:
        statesById.get(peerComponentId(peer.name)) ??
        (peer.interfaceCount === 0 ? ("unhealthy" as RollupState) : ("unknown" as RollupState)),
    })),
  );
}

/** One roster entry after its health has been joined on — the composer's input. */
interface JoinedEntry {
  name: string;
  typeId: string | null;
  state: RollupState;
}

/**
 * The shared stack geometry, level-agnostic by construction.
 *
 * Both levels draw the same box at the same pitch against the same cap, so the
 * geometry exists once and the callers above differ only in how they resolved
 * each entry's state. Duplicating it per level is how the two would drift
 * (CLAUDE.md §4.4), and the geometry is what every alignment assertion in
 * ``status-topology.test.ts`` is written against.
 *
 * Returns ``null`` for a node that hosts no stack (no ``interfaceEdge``).
 */
function composeStack(
  node: TopologyNode,
  level: StackLevel,
  entries: readonly JoinedEntry[],
): InterfaceStack | null {
  const side = node.interfaceEdge;
  if (side === undefined) return null;

  const excluded: ConfiguredInterface[] = [];
  const joined = entries.map((entry) => ({ iface: entry, state: entry.state }));

  // The cap counts ICONS, and the `+K more` affordance sits BELOW them rather than
  // consuming a slot (architect, 2026-08-01 — REQ-STATUS-040 as written stands over the
  // mock's "the tenth slot then becomes +K more"). So an overflowing stack draws
  // CAP icons plus one affordance row: 11 rows at a cap of 10, not 10. Reading the cap
  // as a total slot count would silently make it 9 the moment overflow occurred.
  const overflows = joined.length > INTERFACE_ICON_CAP;
  const visible = overflows
    ? [...joined]
        .sort((a, b) => rollupDisplayRank(a.state) - rollupDisplayRank(b.state))
        .slice(0, INTERFACE_ICON_CAP)
    : joined;
  const overflowCount = overflows ? joined.length - INTERFACE_ICON_CAP : 0;

  const slots = visible.length + (overflows ? 1 : 0);
  // `slots * pitch - gap` is -4 at zero slots, which drew an inside-out
  // separator rule. Zero is not hypothetical: REQ-STATUS-041 makes the Egress
  // node structural precisely so a deployment with no configured interfaces can
  // still reach the management surface, and that deployment is the empty case.
  const height = slots === 0 ? 0 : slots * INTERFACE_ICON_PITCH - INTERFACE_ICON.gap;
  const top = node.cy - height / 2;

  const x =
    side === "left"
      ? node.x - 2 * INTERFACE_STACK_SEP_GAP - INTERFACE_ICON.width
      : node.x + node.w + 2 * INTERFACE_STACK_SEP_GAP;
  const separatorX =
    side === "left" ? node.x - INTERFACE_STACK_SEP_GAP : node.x + node.w + INTERFACE_STACK_SEP_GAP;

  const icons: InterfaceIcon[] = visible.map((entry, index) => ({
    name: entry.iface.name,
    label: truncateInterfaceLabel(entry.iface.name),
    typeId: entry.iface.typeId,
    state: entry.state,
    x,
    y: top + index * INTERFACE_ICON_PITCH,
    width: INTERFACE_ICON.width,
    height: INTERFACE_ICON.height,
  }));

  return {
    parentId: node.id,
    level,
    side,
    icons,
    overflow: overflows
      ? {
          count: overflowCount,
          label: `+${overflowCount} more`,
          x,
          y: top + visible.length * INTERFACE_ICON_PITCH,
          width: INTERFACE_ICON.width,
          height: INTERFACE_ICON.height,
        }
      : null,
    slots,
    x,
    top,
    height,
    bottom: top + height,
    separatorX,
    // The rule spans the STACK, so its length carries information: a short tick
    // at one interface, a full-height rule at the cap. Sharing a centre with the
    // stack also makes it one centring rather than two competing ones.
    separatorTop: top,
    separatorBottom: top + height,
    excluded,
  };
}

// ---------------------------------------------------------------------------
// Extent
// ---------------------------------------------------------------------------

/** The drawing's outer extent for one composition (:func:`composeExtent`). */
export interface DiagramExtent {
  viewBox: { width: number; height: number };
  /**
   * The smallest ``top`` any composed stack reached, in viewBox units.
   *
   * Reported rather than acted on. The extent grows **downward**, so a stack
   * that reaches above y 0 cannot be rescued by growing — it is a composition
   * that no longer fits, and the honest response is to surface it rather than
   * silently render a clipped stack. Callers (and the test suite) use this to
   * assert the top margin; see :data:`INTERFACE_ICON_CAP` for the arithmetic
   * that makes this the binding constraint on the cap.
   */
  minStackTop: number;
}

/**
 * Size the drawing to what actually composed.
 *
 * The interface stacks are config-derived, so the canvas has to be: a
 * deployment near the display cap draws a stack taller than its parent, and a
 * fixed viewBox would clip it. :data:`DIAGRAM_VIEWBOX` is therefore a **floor**
 * — a deployment that composes nothing unusual renders geometry identical to
 * the mock's.
 *
 * **Height only.** The drawing's left and right extremes are the two interface
 * stacks (x 24 and x 1046, against a 1070 viewBox); there is no width slack, so
 * a composition that wanted more has to be re-laid out rather than quietly
 * widened — which is exactly what the 2026-08-01 icon widening did (viewBox
 * 1024 → 1070, every node +23), rather than letting the stacks run off the
 * canvas. No plane bands are grown because none are drawn — the plane
 * *grouping* survives as a textual rollup only (module docstring, point 1).
 */
export function composeExtent(
  nodes: readonly TopologyNode[],
  stacks: readonly InterfaceStack[] = [],
): DiagramExtent {
  const bottoms = [...nodes.map((n) => n.y + n.h), ...stacks.map((s) => s.bottom)];
  const height = Math.max(
    DIAGRAM_VIEWBOX.height,
    ...bottoms.map((bottom) => Math.ceil(bottom + DIAGRAM_MARGIN)),
  );
  // `Infinity` for a composition with no stacks: there is no top to be clipped,
  // and a sentinel that trivially satisfies "top >= margin" is the honest floor.
  const minStackTop = stacks.length === 0 ? Infinity : Math.min(...stacks.map((s) => s.top));
  return { viewBox: { width: DIAGRAM_VIEWBOX.width, height }, minStackTop };
}

// ---------------------------------------------------------------------------
// Connections (carried forward unchanged)
// ---------------------------------------------------------------------------

/**
 * Derive a connection summary's stoplight from connection counts (storyboard
 * §3). ``down > 0`` degrades (unhealthy when nothing is active); absent counts →
 * ``unknown`` (never a false green — REQ-STATUS-016).
 *
 * ``parentState`` (the owning connector's own rollup) makes two guarantees the
 * counts cannot:
 *
 *   - **A standby parent forces standby.** A stopped Peer Interface holds no
 *     transport slots, so it reports ``0/0/0`` — which the count rule reads as
 *     "nothing is down, all good" and paints GREEN beside its grey parent. That
 *     is a false green on the very control this feature exists to provide
 *     (ADR-036 §D-7; Flow L §11). Checked first, because ``standby`` carries the
 *     lowest display priority and :func:`worstRollupState` would drop it.
 *   - **A summary is never healthier than its parent.** If the connector
 *     process is down, its connections are not fine however they last counted.
 *
 * Omitting ``parentState`` keeps the pure count derivation, for callers that
 * have no parent to consult.
 */
export function connectionsSummaryState(
  total: number | null | undefined,
  active: number | null | undefined,
  down: number | null | undefined,
  parentState?: RollupState,
): RollupState {
  if (parentState === "standby") return "standby";
  const derived = ((): RollupState => {
    if (total === null || total === undefined) return "unknown";
    if (down !== null && down !== undefined && down > 0) {
      if (active === 0) return "unhealthy";
      return "degraded";
    }
    return "healthy";
  })();
  return parentState === undefined ? derived : worstRollupState([derived, parentState]);
}

/**
 * The count-summary line a connections section shows (e.g.
 * ``12 conns · 11 active · 1 down``; storyboard §3). Elides the segments whose
 * counts are not collected.
 *
 * A ``standby`` parent prefixes the word: ``0 conns · 0 active`` alone reads as
 * a healthy idle edge, and REQ-UI-046 forbids leaning on the grey to say
 * otherwise. Only standby is prefixed — every other state already reads off the
 * counts themselves.
 */
export function connectionsSummaryText(
  total: number | null | undefined,
  active: number | null | undefined,
  down: number | null | undefined,
  parentState?: RollupState,
): string {
  const base = ((): string => {
    if (total === null || total === undefined) return "not collected";
    const parts = [`${total} ${total === 1 ? "conn" : "conns"}`];
    if (active !== null && active !== undefined) parts.push(`${active} active`);
    if (down !== null && down !== undefined && down > 0) parts.push(`${down} down`);
    return parts.join(" · ");
  })();
  return parentState === "standby" ? `standby · ${base}` : base;
}

/**
 * The component kinds that carry external connections (ingress / egress
 * connectors; storyboard §4 section 5 / §5). Their component detail (Frame 2)
 * gains a Connections section.
 *
 * This must stay in step with the collector's ``_CONNECTOR_KINDS``
 * (:file:`packages/meridian_status_collector/src/meridian_status_collector/_store.py`),
 * which decides whether ``conn_*`` is populated at all: a kind listed here but
 * not there renders a connections section that is permanently "not collected".
 */
export const CONNECTOR_KINDS: ReadonlySet<ComponentKind> = new Set([
  "peer_manager",
  "peer_sapient",
  "peer_raw",
  // `peer` and the two provider kinds were MISSING here while the collector
  // listed them, which is the drift this docstring warns about running the other
  // way: the collector populates `conn_*` for them, and the pane declined to
  // render the section that would have shown it. `sapient_ingress` is gone with
  // its kind (migration 067).
  "peer",
  "provider_manager",
  "provider",
]);

/**
 * The component kinds whose detail pane offers the **Peer Manager** management
 * affordance (ADR-036 §D-5; feature-spec §2.9).
 *
 * The service's own kind plus the two per-interface egress kinds. ``peer_manager``
 * is here (where ``peer_cds`` used to be) because the CDS interfaces are ordinary
 * ``control_plane.peer_interfaces`` rows the management surface may edit, start and
 * stop like any other (§D-5 — "no read-only class of interface"), and the service's
 * pane is where an operator reaches them.
 *
 * The **ingress** kinds live in {@link PROVIDER_KINDS} below — a separate set,
 * because the two surfaces are separate (Flow W and Flow V).
 */
export const PEER_KINDS: ReadonlySet<ComponentKind> = new Set([
  "peer_manager",
  "peer_sapient",
  "peer_raw",
]);

/**
 * Whether a node sits on the egress side, so its detail pane offers the
 * management affordance.
 */
export function isPeerNode(node: TopologyNode): boolean {
  return node.kinds.some((kind) => PEER_KINDS.has(kind));
}

/**
 * The component kinds whose node hosts the **Provider** configuration surface
 * (ADR-040 §D-1 / §D-9; storyboard Flow V §3).
 *
 * ⚠ **These are today's ingress kinds, not tomorrow's.** REQ-UI-163 specifies a
 * dedicated **Provider Manager** node carrying a per-provider icon stack, backed
 * by `provider_manager` / `provider` component kinds. Those kinds are not in this
 * build's {@link ComponentKind} union yet, and composing that node is REQ-UI-163's
 * own work. Until it lands, the Ingress node is where an operator reasons about
 * what feeds the deployment, so it is where the surface is reached from — and
 * when the Provider Manager node arrives, adding its kinds here is the whole
 * change on this side.
 *
 * Keyed on kinds rather than on the node id for {@link isPeerNode}'s reason: the
 * id is a layout identifier that has been renamed before, while a kind is the
 * wire's own vocabulary.
 */
export const PROVIDER_KINDS: ReadonlySet<ComponentKind> = new Set(["provider_manager", "provider"]);

/**
 * Whether a node sits on the ingress side, so its detail pane offers the
 * **Providers…** affordance (REQ-UI-156).
 *
 * The affordance is present on the node whether or not any provider is
 * configured, and that is load-bearing: a deployment that has configured no
 * providers is exactly the deployment where an operator needs to add the first
 * one, and it is also the deployment collecting nothing.
 */
export function isProviderNode(node: TopologyNode): boolean {
  return node.kinds.some((kind) => PROVIDER_KINDS.has(kind));
}

/**
 * The kinds whose ``component_id`` **is** the operator's interface name, so a
 * detail row can pre-select that interface in the management surface.
 *
 * Only the direct-egress kinds: ``meridian_peer_manager`` reports one component
 * per configured interface, named by the interface (``_interface_status.py`` —
 * "the name IS the operator-facing identity of the flow"). ``peer_manager``
 * reports as a single component whose id is a service identity, not an
 * interface name, so it opens the surface unscoped rather than pre-selecting
 * something that does not exist.
 */
export const INTERFACE_NAMED_PEER_KINDS: ReadonlySet<ComponentKind> = new Set([
  "peer_sapient",
  "peer_raw",
]);

/**
 * The component kind whose connections a node owns, or null when it has none.
 * Used to resolve the connections owner's ``component_id`` for the lazy
 * per-connection fetch (:func:`useComponentConnections`).
 *
 * The node's **declaration order** decides which kind wins where a node covers
 * several — Ingress names SAPIENT ingress first, Egress names CDS egress first
 * — so the choice is visible in the node table rather than buried here.
 */
export function nodeConnectionsKind(node: TopologyNode): ComponentKind | null {
  return node.kinds.find((kind) => CONNECTOR_KINDS.has(kind)) ?? null;
}
